import { useQuery } from '@tanstack/react-query';
import { ussd } from '@repletoryreach/sdk-api';

/**
 * Whether USSD exists for this account, asked once and shared.
 *
 * USSD codes are applied for here and answered by the business's flows
 * (4 October 2026). The section shows when USSD is open, and also when the
 * business already has a code or an application, so a code given out before
 * the platform closed applications is never hidden from its owner. Anything
 * short of a clear answer (a failed request, a service that is not deployed)
 * counts as nothing to show, the same rule as SMS, email and Voice.
 */
export function useUssdStatus() {
  const q = useQuery({
    queryKey: ['ussd-status'],
    queryFn: () => ussd.getStatus(),
    staleTime: 60_000,
    retry: false
  });
  const status = q.data?.data ?? null;
  // An answer without the lists (an older or half-deployed service) is nothing to show, never a crashed page.
  const hasAny = (status?.codes?.length ?? 0) > 0 || (status?.applications?.length ?? 0) > 0;
  return {
    status,
    available: status?.available === true,
    /** Open, or something of the business's own to show. */
    shown: status !== null && (status.available === true || hasAny),
    isPending: q.isPending,
    refetch: q.refetch
  };
}
